import { Controller } from "@hotwired/stimulus";

const SEARCH_DEBOUNCE_MS = 250;

// Drives the "Mark as Paid" modal. A single modal instance is shared across all
// company rows; the triggering row button supplies the company context (id, name
// and currently linked payment) via data attributes, which we read from the
// Bootstrap `show.bs.modal` event's relatedTarget.
//
// The admin layout opts out of Turbo Drive (body[data-turbo="false"]), so Turbo
// ignores `data-turbo-frame` on forms and links here. Filtering therefore drives
// the results frame's `src` directly, the way the other admin filters do.
export default class extends Controller {
  static targets = [
    "companyName", "searchInput", "results",
    "linkedContainer", "linkedSlug", "unlinkForm"
  ];

  static outlets = ["filter-popover"];

  static values = { baseUrl: String };

  disconnect() {
    clearTimeout(this._searchTimeout);
  }

  open(event) {
    const { companyId, companyName, paymentSlug } = event.relatedTarget?.dataset || {};
    if (!companyId) return;

    this.companyId = companyId;
    this.companyNameTarget.textContent = companyName || "this company";
    this.unlinkFormTarget.setAttribute("action", this._pilotPaymentUrl(companyId));
    this._showLinkedPayment(paymentSlug);

    this._resetSearchInput();
    this._resetTypeFilter();
    this._loadResults();
  }

  focusSearch() {
    this.searchInputTarget.focus();
  }

  search() {
    clearTimeout(this._searchTimeout);
    this._searchTimeout = setTimeout(() => this._loadResults(), SEARCH_DEBOUNCE_MS);
  }

  // Without a surrounding form Enter does nothing on its own, but admins expect
  // it to commit the query — so run the pending search immediately.
  onKeydown(event) {
    if (event.key !== "Enter") return;

    event.preventDefault();
    clearTimeout(this._searchTimeout);
    this._loadResults();
  }

  applyTypeFilter(event) {
    this.selectedPaymentTypes = event.detail.values || [];
    this._loadResults();
  }

  _resetSearchInput() {
    this.searchInputTarget.value = "";
    // Announce it like a user edit so the field's clear affordance updates, then
    // drop the search that queues off it — opening loads the unfiltered results.
    this.searchInputTarget.dispatchEvent(new Event("input"));
    clearTimeout(this._searchTimeout);
  }

  // The popover is rendered once for the shared modal, so a selection made for
  // one company would otherwise still be applied when the next one opens.
  _resetTypeFilter() {
    this.selectedPaymentTypes = [];
    this.filterPopoverOutlets.forEach(popover => popover.reset());
  }

  _showLinkedPayment(paymentSlug) {
    this.linkedSlugTarget.textContent = paymentSlug || "";
    this.linkedContainerTarget.classList.toggle("d-none", !paymentSlug);
  }

  // Reopening a company with the same filters leaves `src` untouched, so nothing
  // would refetch — reload explicitly to keep a reopened modal from showing
  // stale results.
  _loadResults() {
    if (!this.companyId) return;

    const url = this._searchUrl();

    if (this.resultsTarget.getAttribute("src") === url) {
      this.resultsTarget.reload();
    } else {
      this.resultsTarget.setAttribute("src", url);
    }
  }

  _searchUrl() {
    const url = new URL(`${this._pilotPaymentUrl(this.companyId)}/search`, window.location.origin);
    const query = this.searchInputTarget.value.trim();

    if (query) url.searchParams.set("query", query);
    (this.selectedPaymentTypes || []).forEach(type => {
      url.searchParams.append("payment_type[]", type);
    });

    return `${url.pathname}${url.search}`;
  }

  _pilotPaymentUrl(companyId) {
    return `${this.baseUrlValue}/${companyId}/pilot_payment`;
  }
}
